import { getTimestamp, getTimestamps } from '@shared/helpers/date.helpers';
import { isEqualIds, toLookup } from '@shared/helpers/utils.helpers';
import { getId, isPrimitive } from '@shared/helpers/object.helpers';
import { VERSION_MISMATCH_PATH } from '../helpers/app-version-reload.helpers';
import {
  CollectionTypes,
  UpdateClientCollectionData,
  UpdateClientData,
  UpdateClientDataFn,
  UpdateClientAction,
} from './store.model';

const VERSION_MISMATCH_REDIRECT_KEY = 'nodon_version_mismatch_redirect';
const MAINTENANCE_PATH = '/maintenance';

/**
 * Redirect to /maintenance when the server responds 503 (maintenance mode).
 * Used by the axios 503 interceptors.
 */
export const redirectToMaintenance = () => {
  if (window.location.pathname === MAINTENANCE_PATH) return;
  window.location.replace(MAINTENANCE_PATH);
};

/** Builds the /new-version URL for version-mismatch handling. */
const buildVersionMismatchUrl = (
  appVersion: string,
  returnPath: string,
  returnSearch: string,
): string => {
  const searchParams = new URLSearchParams();
  searchParams.set('reloadVersion', appVersion);
  searchParams.set('returnPath', returnPath);
  searchParams.set('returnSearch', returnSearch);
  searchParams.set('r', Math.random().toString(32));
  return `${VERSION_MISMATCH_PATH}?${searchParams.toString()}`;
};

/**
 * Clears the version-mismatch redirect flag so a future mismatch can go through /new-version again.
 * Call after a successful version-reload cycle (e.g. when {@link VersionChecker} confirms version matches).
 */
export const clearVersionMismatchRedirectFlag = () => {
  sessionStorage.removeItem(VERSION_MISMATCH_REDIRECT_KEY);
};

/**
 * Redirect to /new-version on version mismatch.
 * Repeated mismatch also goes to /new-version (not /not-found) to avoid showing a 404.
 * Used by both the axios 409 interceptor and {@link VersionChecker}.
 */
export const forceReloadApp = (appVersion: string) => {
  const { pathname, search } = window.location;
  // Strict pathname checks to avoid false positives (e.g. /new-version-history, /not-found-corp/projects/...)
  if (pathname === VERSION_MISMATCH_PATH) return;
  if (pathname === '/not-found') return;

  const redirectUrl = buildVersionMismatchUrl(appVersion, pathname, search);
  const alreadyRedirected = sessionStorage.getItem(
    VERSION_MISMATCH_REDIRECT_KEY,
  );
  if (alreadyRedirected) {
    window.location.replace(redirectUrl);
    return;
  }

  sessionStorage.setItem(VERSION_MISMATCH_REDIRECT_KEY, '1');
  window.location.href = redirectUrl;
};

export const getErrorMessage = (
  action: 'get' | 'create' | 'update' | 'delete',
  resource: string,
): string => `Could not ${action} ${resource}`;

const toCollectionData = <T extends CollectionTypes>(
  collection: T[],
): UpdateClientCollectionData<T> => {
  return {
    collection,
    lookup: toLookup(collection),
  };
};

const defaultCollectionData = { collection: undefined, lookup: undefined };

const applyActionToCollectionData: UpdateClientDataFn = ({
  action,
  itemOrId,
  collection,
  lookup,
}) => {
  if (!collection && !lookup) {
    return defaultCollectionData;
  }

  const collectionData = collection ?? Object.values(lookup ?? {});

  if (!action || !itemOrId) {
    return toCollectionData(collectionData);
  }

  const id = getId(itemOrId);

  if (
    action === UpdateClientAction.Add ||
    action === UpdateClientAction.Update
  ) {
    if (isPrimitive(itemOrId)) {
      throw new Error('Cant add primitive to collection');
    }

    return toCollectionData([
      ...collectionData.filter((item) => !isEqualIds(item.id, id)),
      itemOrId,
    ]);
  }
  if (action === UpdateClientAction.Remove) {
    return toCollectionData(
      collectionData.filter((item) => !isEqualIds(item.id, id)),
    );
  }
  return defaultCollectionData;
};

// Typed as an explicit generic instead of UpdateClientDataFn: TS 7 (tsgo RC)
// cannot unify generic signatures whose parameter is an intersection type
export const updateResourceLocally = <T extends CollectionTypes>({
  action,
  itemOrId,
  collection,
  lookup,
}: UpdateClientData<T> &
  UpdateClientCollectionData<T>): UpdateClientCollectionData<T> => {
  switch (action) {
    case UpdateClientAction.Add: {
      if (isPrimitive(itemOrId)) {
        throw new Error('Cant add primitive to collection');
      }

      const timestamped = { ...itemOrId, ...getTimestamps() };

      return applyActionToCollectionData<T>({
        action,
        itemOrId: timestamped,
        collection,
        lookup,
      });
    }
    case UpdateClientAction.Update: {
      if (isPrimitive(itemOrId)) {
        throw new Error('Cant update primitive');
      }

      const timestamped = { ...itemOrId, updated_at: getTimestamp() };

      return applyActionToCollectionData<T>({
        action,
        itemOrId: timestamped,
        collection,
        lookup,
      });
    }
    case UpdateClientAction.Remove: {
      return applyActionToCollectionData<T>({
        action,
        itemOrId,
        collection,
        lookup,
      });
    }
    default:
      return { collection, lookup };
  }
};
